import { useEffect, useState } from "react";
import { BarChart3, ChevronDown, ClipboardCheck, Clock3, FileText, Gavel, Plus, ShieldCheck, Sparkles, Truck, PackageCheck, AlertTriangle } from "lucide-react";
import { money, statusLabel, Countdown, formatRelative, Stat, Empty, qtyLabel } from "./shared";
import { useAuth } from "../../auth/AuthContext";

// ============================================================
// VIEW: OVERVIEW
// ============================================================
function TodayIST() {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => { const t = setInterval(() => setNow(new Date()), 60000); return () => clearInterval(t); }, []);
  const label = now.toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long", year: "numeric", timeZone: "Asia/Kolkata" });
  const time = now.toLocaleTimeString("en-IN", { hour: "numeric", minute: "2-digit", timeZone: "Asia/Kolkata" });
  return <div className="date-stamp" data-testid="overview-date-stamp"><Clock3 size={15}/> {label} · {time} IST</div>;
}

export function Overview({ role, metrics, jobs, briefs, setView, openJob, setModal, focusBrief }) {
  const action = role === "admin" ? jobs.filter(j => j.status === "pending_review") : jobs.filter(j => ["live", "awarded", "dispatched"].includes(j.status));
  return (
    <section className="content">
      <div className="hero-row">
        <div>
          <p className="eyebrow">{role === "admin" ? "CONTROL TOWER" : "PROCUREMENT CONTROL"}</p>
          <h2>{role === "admin" ? "Clear the runway." : "Keep every run moving."}</h2>
          <p className="subhead">
            {role === "admin" ? "Review, refine, set price floors, and release specifications to the vetted network." :
             role === "printer" ? "Sealed auctions matched to your press capabilities. One private bid per job." :
             "Anonymous bids, escrow-protected payouts, and full delivery tracking in one workspace."}
          </p>
        </div>
        <TodayIST/>
      </div>
      <div className="stat-grid">
        {role === "agency" ? (
          <>
            <Stat icon={Sparkles} label="Active Briefs" value={metrics.active_briefs || 0} note="in progress"/>
            <Stat icon={ClipboardCheck} label="Client Feedback" value={metrics.awaiting_client_feedback || 0} note="awaiting approval" accent/>
            <Stat icon={PackageCheck} label="Ready for Print" value={metrics.ready_for_print || 0} note="approved artworks"/>
            <Stat icon={BarChart3} label="Earnings This Month" value={money(metrics.earnings_this_month || 0)} note="net payout"/>
          </>
        ) : role === "admin" ? (
          <>
            <Stat icon={ClipboardCheck} label="Pending Review" value={metrics.pending_review || 0} note="specs awaiting approval" accent/>
            <Stat icon={ShieldCheck} label="KYC Approvals" value={metrics.kyc_pending || 0} note="pending verification"/>
            <Stat icon={AlertTriangle} label="Active Disputes" value={metrics.active_disputes || 0} note="requires moderation"/>
            <Stat icon={BarChart3} label="GMV / Platform Volume" value={money(metrics.gmv_this_month || 0)} note="this month"/>
          </>
        ) : (
          <>
            <Stat icon={Gavel} label="Live RFQs" value={metrics.open_auctions || 0} note="receiving supplier bids"/>
            {role === "printer" ? (
              <Stat icon={ClipboardCheck} label="Active Bids" value={metrics.pending_review || 0} note="pending decision" accent/>
            ) : (
              <Stat icon={ClipboardCheck} label="Action Needed" value={metrics.pending_review || 0} note="pending your selection" accent/>
            )}
            <Stat icon={Truck} label="In Production" value={metrics.in_production || 0} note="track active orders"/>
            {role === "printer" ? (
              <Stat icon={BarChart3} label="Revenue Earned" value={money(metrics.saved_this_month)} note={`won this month · ${money(metrics.saved_lifetime)} lifetime`}/>
            ) : (
              <Stat icon={BarChart3} label="Cost Savings" value={money(metrics.saved_this_month)} note={`saved this month · ${money(metrics.saved_lifetime)} lifetime`}/>
            )}
          </>
        )}
      </div>
      <div className="section-heading">
        <div>
          <p className="eyebrow">{role === "admin" ? "INBOX" : "WORK IN PROGRESS"}</p>
          <h3>{role === "admin" ? "Jobs waiting for review" : "Your active work"}</h3>
        </div>
        {role === "buyer" && (
          <button data-testid="new-job-button" className="primary-btn" onClick={() => setModal("job")}>
            <Plus size={16}/> New request
          </button>
        )}
      </div>
      <div className="work-grid">
        <div className="list-panel">
          {action.length ? action.slice(0, 4).map(j => <JobRow key={j.id} job={j} openJob={openJob}/>) : <Empty text="Nothing needs attention right now."/>}
        </div>
        {role !== "printer" && (
          role === "buyer"
            ? <BuyerArtworkActionFeed briefs={briefs} setView={setView} focusBrief={focusBrief} setModal={setModal}/>
            : (
              <div className="side-panel">
                <div className="panel-title">
                  <span>Artwork pipeline</span>
                  <button data-testid="view-briefs-button" onClick={() => setView("briefs")}>View all</button>
                </div>
                {briefs.slice(0, 3).map(b => (
                  <div className="brief-mini" key={b.id}>
                    <div className="mini-icon"><Sparkles size={15}/></div>
                    <div><b>{b.title}</b><span>{b.brand_name} · {statusLabel[b.status] || b.status}</span></div>
                  </div>
                ))}
              </div>
            )
        )}
      </div>
    </section>
  );
}

// ============================================================
// BUYER · Artwork pipeline — action-first feed
// ============================================================
// Instead of listing the latest 3 briefs, surface the briefs that need the
// buyer to make a decision RIGHT NOW. Priority ladder:
//   P0 "delivered"   → proof awaiting your approval (amber)
//   P1 "quoted"      → fresh agency quotes to pick (purple)
//   P2 "in_progress" → in design, nearing deadline (blue)
// If nothing actionable, fall back to a 1-click "Start a brief" CTA.
export const ACTION_PRIORITY = {
  delivered:   { rank: 0, tone: "warn",   cta: "Review proof",   note: (b) => `${b.awarded_agency_name || "Your agency"} sent the proof` },
  quoted:      { rank: 1, tone: "action", cta: "Pick a quote",   note: (b) => `${(b.quotes || []).length || "New"} quotes awaiting your pick` },
  revision:    { rank: 2, tone: "info",   cta: "Message agency", note: () => "Revisions in progress" },
  in_progress: { rank: 3, tone: "info",   cta: "View brief",     note: (b) => `${b.awarded_agency_name || "Your agency"} is designing` },
  proofing:    { rank: 4, tone: "info",   cta: "View brief",     note: () => "In pre-press / proofing" },
};

export const TONE_STYLE = {
  warn:   { bg:"#FFFBEB", bd:"#FDE68A", fg:"#92400E", dot:"#F59E0B" },
  action: { bg:"#F3E8FF", bd:"#D8B4FE", fg:"#6B21A8", dot:"#A855F7" },
  info:   { bg:"#EFF6FF", bd:"#BFDBFE", fg:"#1E40AF", dot:"#3B82F6" },
};

export function BuyerArtworkActionFeed({ briefs, setView, focusBrief, setModal }) {
  const ranked = (briefs || [])
    .filter(b => ACTION_PRIORITY[b.status])
    .sort((a, b) => {
      const ra = ACTION_PRIORITY[a.status]?.rank ?? 99;
      const rb = ACTION_PRIORITY[b.status]?.rank ?? 99;
      if (ra !== rb) return ra - rb;
      // Within the same bucket, newest activity first
      const ta = new Date(a.updated_at || a.created_at || 0).getTime();
      const tb = new Date(b.updated_at || b.created_at || 0).getTime();
      return tb - ta;
    })
    .slice(0, 3);

  return (
    <div className="side-panel" data-testid="buyer-artwork-action-feed">
      <div className="panel-title">
        <span>Artwork pipeline</span>
        <button data-testid="view-briefs-button" onClick={() => setView("briefs")}>View all</button>
      </div>

      {ranked.length === 0 ? (
        <div
          data-testid="artwork-feed-empty"
          style={{
            padding:"18px 14px", textAlign:"center",
            border:"1px dashed #CBD5E1", borderRadius:10,
            background:"#F8FAFC",
          }}
        >
          <Sparkles size={22} style={{color:"#8B5CF6", margin:"0 auto 8px"}}/>
          <div style={{fontSize:13, fontWeight:700, color:"#0F172A", marginBottom:3}}>No briefs need you right now</div>
          <div style={{fontSize:11, color:"#64748B", marginBottom:12, lineHeight:1.5}}>
            Hire a design agency to turn your brief into press-ready artwork — proofs land here when they need your approval.
          </div>
          <button
            data-testid="artwork-feed-new-brief"
            className="primary-btn"
            style={{fontSize:12, padding:"6px 14px"}}
            onClick={() => setModal && setModal("brief")}
          >Start a brief</button>
        </div>
      ) : (
        <>
          {ranked.map(b => {
            const meta = ACTION_PRIORITY[b.status];
            const tone = TONE_STYLE[meta.tone];
            return (
              <div
                key={b.id}
                data-testid={`artwork-feed-card-${b.id}`}
                data-tone={meta.tone}
                style={{
                  display:"flex", gap:10, padding:"10px 12px",
                  border:`1px solid ${tone.bd}`, background: tone.bg,
                  borderRadius:10, marginBottom:8,
                }}
              >
                <span style={{
                  width:6, flexShrink:0, borderRadius:999,
                  background: tone.dot, alignSelf:"stretch",
                }}/>
                <div style={{flex:1, minWidth:0}}>
                  <div style={{display:"flex", justifyContent:"space-between", gap:8, alignItems:"baseline"}}>
                    <b style={{fontSize:13, color:"#0F172A", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap"}}>{b.title}</b>
                    <small style={{fontSize:10, color: tone.fg, fontWeight:700, textTransform:"uppercase", letterSpacing:0.3, whiteSpace:"nowrap"}}>
                      {statusLabel[b.status] || b.status}
                    </small>
                  </div>
                  <div style={{fontSize:11, color:"#475569", marginTop:2, lineHeight:1.4}}>
                    {meta.note(b)} · <span style={{color:"#94A3B8"}}>{formatRelative(b.updated_at || b.created_at)}</span>
                  </div>
                  <button
                    data-testid={`artwork-feed-cta-${b.id}`}
                    onClick={() => focusBrief && focusBrief(b.id)}
                    style={{
                      marginTop:7, background:"transparent",
                      border:`1px solid ${tone.bd}`, color: tone.fg,
                      padding:"3px 10px", borderRadius:999,
                      fontSize:11, fontWeight:700, cursor:"pointer",
                    }}
                  >{meta.cta} →</button>
                </div>
              </div>
            );
          })}
          <button
            data-testid="artwork-feed-new-brief-footer"
            onClick={() => setModal && setModal("brief")}
            style={{
              width:"100%", marginTop:4, padding:"8px 10px",
              background:"transparent", border:"1px dashed #CBD5E1",
              borderRadius:10, cursor:"pointer",
              fontSize:11, color:"#475569", fontWeight:700,
            }}
          >+ Start another brief</button>
        </>
      )}
    </div>
  );
}

// ============================================================
// VIEW: JOBS
// ============================================================
export function Jobs({ role, jobs, openJob, setModal }) {
  const [filter, setFilter] = useState("all");
  const counts = {
    all: jobs.length,
    live: jobs.filter(j => j.status === "live").length,
    review: jobs.filter(j => j.status === "pending_review").length,
    // "Awarded" bucket keeps in-flight orders (awarded + dispatched) so the
    // row count surfaces things still owed to the buyer. "Completed" is split
    // out as its own pill below so buyers can audit historical fulfilment.
    awarded: jobs.filter(j => ["awarded", "dispatched"].includes(j.status)).length,
    completed: jobs.filter(j => j.status === "completed").length,
  };
  const filtered = jobs.filter(j => {
    if (filter === "all") return true;
    if (filter === "live") return j.status === "live";
    if (filter === "review") return j.status === "pending_review";
    if (filter === "awarded") return ["awarded", "dispatched"].includes(j.status);
    if (filter === "completed") return j.status === "completed";
    return true;
  });
  return (
    <section className="content">
      <div className="section-heading">
        <div>
          <p className="eyebrow">{role === "admin" ? "MODERATION QUEUE" : role === "printer" ? "CAPABILITY-MATCHED" : "PRINT PROCUREMENT"}</p>
          <h2>{role === "admin" ? "Review before release" : role === "printer" ? "Sealed auctions for you" : "My jobs"}</h2>
          <p className="subhead">
            {role === "admin" ? "Every buyer request is refined, priced-floored, and released here." :
             role === "printer" ? "Only auctions matching your machine capabilities. One sealed bid per job." :
             "Track requirements, view anonymous bids, and award with escrow protection."}
          </p>
        </div>
        {role === "buyer" && (
          <button data-testid="jobs-new-request" className="primary-btn" onClick={() => setModal("job")}>
            <Plus size={16}/> New request
          </button>
        )}
      </div>
      <div className="filter-row">
        <button data-testid="filter-all" onClick={() => setFilter("all")} className={`filter${filter === "all" ? " active" : ""}`}>All jobs <b>{counts.all}</b></button>
        <button data-testid="filter-live" onClick={() => setFilter("live")} className={`filter${filter === "live" ? " active" : ""}`}>Live <b>{counts.live}</b></button>
        <button data-testid="filter-review" onClick={() => setFilter("review")} className={`filter${filter === "review" ? " active" : ""}`}>Needs review <b>{counts.review}</b></button>
        <button data-testid="filter-awarded" onClick={() => setFilter("awarded")} className={`filter${filter === "awarded" ? " active" : ""}`}>Awarded <b>{counts.awarded}</b></button>
        <button data-testid="filter-completed" onClick={() => setFilter("completed")} className={`filter${filter === "completed" ? " active" : ""}`}>Completed <b>{counts.completed}</b></button>
      </div>
      <div className="job-table">
        <div className="table-head">
          <span>REQUEST</span><span>SPECIFICATION</span><span>STATUS</span><span>ACTIVITY</span><span></span>
        </div>
        {filtered.length ? filtered.map(j => <JobRow key={j.id} job={j} openJob={openJob} table/>) : <Empty text="No jobs match your view."/>}
      </div>
    </section>
  );
}

export function JobRow({ job, openJob, table }) {
  const role = useAuth().user?.role;
  const isLive = job.status === "live";
  return (
    <button data-testid={`job-row-${job.id}`} className={table ? "job-row table-row" : "job-row"} onClick={() => openJob(job)}>
      <div className="job-title">
        <div className="job-symbol">{job.category === "Packaging" ? "PK" : "PR"}</div>
        <div><b>{job.title}</b><span>{job.id} · {job.category}</span></div>
      </div>
      <div className="spec">
        <b>{qtyLabel(job)}</b>
        <span>{job.dimensions} · {job.stock}</span>
      </div>
      <div>
        <span className={`status ${job.status}`}>{statusLabel[job.status] || job.status}</span>
        {role === "admin" && job.gst_review_flag && <span data-testid={`gst-check-chip-${job.id}`} style={{fontSize:10,fontWeight:700,color:"#B91C1C",background:"#FEE2E2",borderRadius:4,padding:"2px 6px",marginLeft:6}}>GST check</span>}
        {isLive && job.auction_ends_at && <div className="row-countdown"><Countdown endsAt={job.auction_ends_at} size="sm"/></div>}
      </div>
      <div className="activity">
        <b>{job.bid_count || 0} bids</b>
        <span>{["admin", "buyer"].includes(role) ? (job.price_floor > 0 ? `Floor ${money(job.price_floor)}` : "No floor") : ""}</span>
      </div>
      <ChevronDown size={16} className="row-arrow"/>
    </button>
  );
}

// ============================================================
// VIEW: BRIEFS
// ============================================================
export function Briefs({ briefs, setModal, openJob }) {
  return (
    <section className="content">
      <div className="section-heading">
        <div>
          <p className="eyebrow">THREE-SIDED MARKETPLACE</p>
          <h2>Artwork briefs</h2>
          <p className="subhead">Pair a structured creative brief with a vetted design & creative supplier before production starts.</p>
        </div>
        <button data-testid="new-brief-button" className="primary-btn" onClick={() => setModal("brief")}>
          <Plus size={16}/> New brief
        </button>
      </div>
      <div className="brief-grid">
        {briefs.map(b => (
          <div className="brief-card" key={b.id}>
            <div className="brief-card-top">
              <div className="brief-icon"><Sparkles size={19}/></div>
              <span className={`status ${b.status}`}>{statusLabel[b.status] || b.status}</span>
            </div>
            <h3>{b.title}</h3>
            <p>{b.brand_name}</p>
            <div className="brief-details">
              <span><FileText size={14}/>{b.dimensions}</span>
              <span><Sparkles size={14}/>{b.substrate}</span>
            </div>
            <div className="brief-footer">
              <span>Structured brief</span>
              <button data-testid={`brief-view-${b.id}`} onClick={() => openJob(b)}>Open brief <ChevronDown size={14}/></button>
            </div>
          </div>
        ))}
        <div className="brief-card add-card" onClick={() => setModal("brief")}>
          <Plus size={22}/>
          <b>Create an artwork brief</b>
          <span>Give agencies the context they need</span>
        </div>
      </div>
    </section>
  );
}
